import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { clinicsAPI, authAPI } from '../services/api'
import './ClinicFormPage.css'

function ClinicFormPage() {
  const { id } = useParams()
  const isEdit = !!id
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    nip: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    is_active: 1,
  })
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(isEdit)
  const [error, setError] = useState('')
  
  useEffect(() => {
    // Check authentication
    authAPI.checkAuth().then(isAuth => {
      if (!isAuth) {
        navigate('/login')
        return
      }
      if (isEdit) {
        loadClinic()
      }
    }).catch(err => {
      console.error('Auth check error:', err)
      navigate('/login')
    })
  }, [id, isEdit, navigate])
  
  const loadClinic = async () => {
    try {
      setLoadingData(true)
      const data = await clinicsAPI.getClinic(id)
      const clinic = data.clinic
      setFormData({
        name: clinic.name || '',
        code: clinic.code || '',
        nip: clinic.nip || '',
        address: clinic.address || '',
        contact_email: clinic.contact_email || '',
        contact_phone: clinic.contact_phone || '',
        is_active: clinic.is_active === 1 ? 1 : 0,
      })
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Błąd ładowania kliniki.'
      setError(errorMsg)
      console.error('Load clinic error:', err)
    } finally {
      setLoadingData(false)
    }
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    
    try {
      if (isEdit) {
        await clinicsAPI.updateClinic(id, formData)
      } else {
        await clinicsAPI.createClinic(formData)
      }
      navigate('/dashboard')
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || `Błąd ${isEdit ? 'aktualizacji' : 'tworzenia'} kliniki.`
      setError(errorMsg)
      console.error('Submit error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }))
  }
  
  if (loadingData) {
    return (
      <div className="clinic-form-page">
        <div className="loading">Ładowanie...</div>
      </div>
    )
  }
  
  return (
    <div className="clinic-form-page">
      <header className="form-header">
        <div className="logo">WELLYSA</div>
        <h1>{isEdit ? 'Edytuj Klinikę' : 'Nowa Klinika'}</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          Anuluj
        </button>
      </header>
      
      <main className="form-main">
        <form onSubmit={handleSubmit} className="clinic-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-section">
            <h2>Podstawowe Informacje</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Nazwa *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="code">Kod *</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="TEST001"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="nip">NIP</label>
                <input
                  type="text"
                  id="nip"
                  name="nip"
                  value={formData.nip}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Kontakt</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="contact_email">Email</label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="contact_phone">Telefon</label>
                <input
                  type="tel"
                  id="contact_phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Adres</h2>
            <div className="form-group">
              <label htmlFor="address">Adres</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={loading}
                rows="3"
              />
            </div>
          </div>
          
          <div className="form-section">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active === 1}
                  onChange={handleChange}
                  disabled={loading}
                />
                Klinika aktywna
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Zapisywanie...' : (isEdit ? 'Zapisz Zmiany' : 'Utwórz Klinikę')}
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/dashboard')} 
              className="btn-secondary"
              disabled={loading}
            >
              Anuluj
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}

export default ClinicFormPage
